import React, { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Disclosure, Menu } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import SingleEstate from './SingleEstate';
import { useTranslation } from 'react-i18next';

// Helper function to create nice round numbers for ranges
const roundToNice = (num) => {
    if (num <= 0) return 0;
    const magnitude = Math.pow(10, Math.floor(Math.log10(num)));
    const normalized = num / magnitude;

    if (normalized < 2.5) return Math.round(2 * magnitude);
    if (normalized < 5) return Math.round(5 * magnitude);
    return Math.round(10 * magnitude);
};

// Helper to format price in EUR
const formatPrice = (price) => {
    if (price >= 1000000) {
        return `€${(price / 1000000).toFixed(1)}M`;
    }
    if (price >= 1000) {
        return `€${(price / 1000).toFixed(0)}K`;
    }
    return `€${price}`;
};

// Helper to format area
const formatArea = (area) => `${area} m²`;

// Helper to create ranges
const createRanges = (values, steps, formatFn) => {
    if (!values || values.length === 0) return [];
    if (values.length === 1) {
        const value = values[0];
        return [
            {
                value: `0-${value}`,
                label: `${formatFn(0)} - ${formatFn(value)}`
            },
            {
                value: `${value}`,
                label: `Over ${formatFn(value)}`
            }
        ];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    // If min and max are the same, create a single range
    if (min === max) {
        return [{
            value: `${min}`,
            label: formatFn(min)
        }];
    }

    // Determine appropriate number of steps based on data spread
    const actualSteps = Math.min(steps, Math.ceil((max - min) / (min || 1)));

    const ranges = [];
    const range = max - min;
    const step = roundToNice(range / (actualSteps - 1));

    let currentMin = min;
    while (currentMin < max) {
        const currentMax = Math.min(max, currentMin + step);
        ranges.push({
            value: `${currentMin}-${currentMax}`,
            label: `${formatFn(currentMin)} - ${formatFn(currentMax)}`
        });
        currentMin = currentMax;
    }

    // Add the "Over" range if we haven't reached the max
    if (currentMin <= max) {
        ranges.push({
            value: `${currentMin}`,
            label: `Over ${formatFn(currentMin)}`
        });
    }

    return ranges;
};

const filterLabels = {
    bedrooms: 'listings.propertyFeatures.bedrooms',
    type: 'listings.dropdowns.type.title',
    price: 'listings.filters.attributes.price',
    livingArea: 'listings.filters.attributes.livingArea',
    regions: 'listings.dropdowns.region.title'
};

const sortOptions = [
    { name: 'listings.sorting.newest', value: 'newest' },
    { name: 'listings.sorting.priceLowToHigh', value: 'price-asc' },
    { name: 'listings.sorting.priceHighToLow', value: 'price-desc' }
];

export default function EstatesGrid({
                                        estates,
                                        showFilters = true,
                                        showSort = true,
                                        className = "",
                                        gridClassName = "grid grid-cols-1 gap-y-12 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-3 xl:gap-x-8",
                                        selectedEstate = null
                                    }) {
    const { t } = useTranslation('common');
    const [activeFilters, setActiveFilters] = useState({
        bedrooms: [],
        price: [],
        type: [],
        livingArea: [],
        regions: []
    });
    const [sortOption, setSortOption] = useState('newest');

    // Create dynamic filters based on estate data
    const filters = useMemo(() => {
        // Get valid prices and areas, excluding 0, null, and NaN
        const validPrices = estates
            .map(estate => estate.price)
            .filter(price => price != null && !isNaN(price) && price > 0)
            .sort((a, b) => a - b);

        const validAreas = estates
            .map(estate => estate.living_area)
            .filter(area => area != null && !isNaN(area) && area > 0)
            .sort((a, b) => a - b);

        // Get unique room counts
        const uniqueRooms = [...new Set(estates
            .map(estate => estate.bedrooms?.toString())
            .filter(Boolean)
        )].sort((a, b) => Number(a) - Number(b));

        const bedroomFilters = uniqueRooms.map(rooms => ({
            value: rooms,
            label: `${rooms} ${rooms === '1' ? t('listings.plurals.bedrooms', { count: 1 }) : t('listings.plurals.bedrooms', { count: 2 })}`
        }));

        // Get unique property types
        const uniqueTypes = [...new Set(estates
            .map(estate => estate.type?.toLowerCase())
            .filter(Boolean)
        )];

        const typeLabels = {
            villa: t('search.propertyTypes.options.villa'),
            apartment: t('search.propertyTypes.options.apartment'),
            townhouse: t('search.propertyTypes.options.townhouse'),
            penthouse: t('search.propertyTypes.options.penthouse')
        };

        const typeFilters = uniqueTypes.map(type => ({
            value: type,
            label: typeLabels[type] || type
        }));

        // Create ranges only if we have valid data
        const priceRanges = validPrices.length > 0
            ? createRanges(validPrices, 6, formatPrice)
            : [];

        const areaRanges = validAreas.length > 0
            ? createRanges(validAreas, 5, formatArea)
            : [];

        // Get unique regions
        const uniqueRegions = [...new Set(estates
            .map(estate => estate.region?.name)
            .filter(Boolean)
        )].sort();

        const regions = uniqueRegions.map(region => ({
            value: region,
            label: region
        }));

        // Only return filter categories that have options
        const filters = {};
        if (bedroomFilters.length > 0) filters.bedrooms = bedroomFilters;
        if (typeFilters.length > 0) filters.type = typeFilters;
        if (priceRanges.length > 0) filters.price = priceRanges;
        if (areaRanges.length > 0) filters.livingArea = areaRanges;
        if (regions.length > 0) filters.regions = regions;

        return filters;
    }, [estates, t]);

    // Add effect to scroll to selected estate
    useEffect(() => {
        if (selectedEstate) {
            const element = document.getElementById(`estate-${selectedEstate.id}`);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }
    }, [selectedEstate]);

    const matchesRange = (value, range) => {
        if (!value || isNaN(value)) return false;
        const [min, max] = range.split('-').map(Number);
        if (!max) return value >= min;
        return value >= min && value < max;
    };

    const handleFilterChange = (filterType, value, checked) => {
        setActiveFilters(prev => ({
            ...prev,
            [filterType]: checked
                ? [...prev[filterType], value]
                : prev[filterType].filter(v => v !== value)
        }));
    };

    const clearFilters = () => {
        setActiveFilters({
            bedrooms: [],
            price: [],
            type: [],
            livingArea: [],
            regions: []
        });
    };

    // Filter and sort logic
    const filteredAndSortedEstates = useMemo(() => {
        let filtered = estates.filter(estate => {
            // Only check filters that exist and have active values
            if (activeFilters.type.length && filters.type &&
                !activeFilters.type.includes(estate.type?.toLowerCase())) {
                return false;
            }

            if (activeFilters.bedrooms.length && filters.bedrooms &&
                !activeFilters.bedrooms.includes(estate.bedrooms?.toString())) {
                return false;
            }

            if (activeFilters.price.length && filters.price && estate.price) {
                const matchesPrice = activeFilters.price.some(range =>
                    matchesRange(estate.price, range)
                );
                if (!matchesPrice) return false;
            }

            if (activeFilters.livingArea.length && filters.livingArea && estate.living_area) {
                const matchesArea = activeFilters.livingArea.some(range =>
                    matchesRange(estate.living_area, range)
                );
                if (!matchesArea) return false;
            }

            if (activeFilters.regions.length && filters.regions &&
                !activeFilters.regions.includes(estate.region?.name)) {
                return false;
            }

            return true;
        });

        // Sort filtered results
        return filtered.sort((a, b) => {
            switch (sortOption) {
                case 'price-asc':
                    return (a.price || 0) - (b.price || 0);
                case 'price-desc':
                    return (b.price || 0) - (a.price || 0);
                case 'newest':
                default:
                    return new Date(b.created_at || 0) - new Date(a.created_at || 0);
            }
        });
    }, [estates, activeFilters, sortOption, filters]);

    const activeFiltersCount = Object.values(activeFilters).flat().length;

    return (
        <div className={className}>
            {(showFilters || showSort) && (
                <Disclosure as="section" className="grid items-center border-b border-t border-gray-200">
                    {({open}) => (
                        <>
                            <div className="relative col-start-1 row-start-1 py-4">
                                <div className="mx-auto flex max-w-7xl justify-between px-4 sm:px-6 lg:px-8">
                                    {showFilters && (
                                        <div className="flex">
                                            <Disclosure.Button
                                                className="group flex items-center font-medium text-gray-700 text-sm">
                                                {t('listings.filters.showFilters')}
                                                <ChevronDownIcon
                                                    className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                                    aria-hidden="true"
                                                />
                                            </Disclosure.Button>

                                            {activeFiltersCount > 0 && (
                                                <div className="mt-2 sm:mt-0 sm:ml-10">
                                                    <div className="-m-1 flex flex-wrap items-center">
                                                        {Object.entries(activeFilters).map(([type, values]) =>
                                                            values.map(value => {
                                                                const option = filters[type]?.find(opt => opt.value === value);
                                                                const displayLabel = option ? option.label : value;

                                                                return (
                                                                    <span
                                                                        key={`${type}-${value}`}
                                                                        className="m-1 inline-flex items-center rounded-full border border-gray-200 py-1.5 pl-3 pr-2 text-xs bg-gray-50 font-medium"
                                                                    >
                                                                        <span>{displayLabel}</span>
                                                                        <button
                                                                            type="button"
                                                                            className="ml-1 inline-flex h-4 w-4 p-1 text-gray-400 hover:text-gray-500"
                                                                            onClick={(e) => {
                                                                                e.preventDefault();
                                                                                handleFilterChange(type, value, false);
                                                                            }}
                                                                        >
                                                                            <span className="sr-only">
                                                                                {t('listings.filters.removeFilter')} {displayLabel}
                                                                            </span>
                                                                            <svg
                                                                                className="h-2 w-2"
                                                                                stroke="currentColor"
                                                                                fill="none"
                                                                                viewBox="0 0 8 8"
                                                                            >
                                                                                <path
                                                                                    strokeLinecap="round"
                                                                                    strokeWidth="1.5"
                                                                                    d="M1 1l6 6m0-6L1 7"
                                                                                />
                                                                            </svg>
                                                                        </button>
                                                                    </span>
                                                                );
                                                            })
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {showSort && (
                                        <Menu as="div" className="relative inline-block text-left">
                                            <Menu.Button
                                                className="group inline-flex justify-center text-sm font-medium text-gray-700 hover:text-gray-900">
                                                {t('listings.sorting.title', 'Sort')}
                                                <ChevronDownIcon
                                                    className="-mr-1 ml-1 h-5 w-5 flex-shrink-0 text-gray-400 group-hover:text-gray-500"
                                                    aria-hidden="true"
                                                />
                                            </Menu.Button>

                                            <Menu.Items
                                                className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                <div className="py-1">
                                                    {sortOptions.map((option) => (
                                                        <Menu.Item key={option.value}>
                                                            {({active}) => (
                                                                <button
                                                                    onClick={() => setSortOption(option.value)}
                                                                    className={`${
                                                                        active ? 'bg-gray-100' : ''
                                                                    } ${
                                                                        sortOption === option.value ? 'font-medium text-gray-900' : 'text-gray-500'
                                                                    } block px-4 py-2 text-sm w-full text-left`}
                                                                >
                                                                    {t(option.name)}
                                                                </button>
                                                            )}
                                                        </Menu.Item>
                                                    ))}
                                                </div>
                                            </Menu.Items>
                                        </Menu>
                                    )}
                                </div>
                            </div>

                            <Disclosure.Panel className="border-t border-gray-200 py-10">
                                <div
                                    className="mx-auto grid max-w-7xl grid-cols-2 gap-x-4 gap-y-10 px-4 text-sm sm:px-6 md:grid-cols-5 md:gap-x-6 lg:px-8">
                                    {Object.entries(filters).map(([filterType, options]) => (
                                        <fieldset key={filterType}>
                                            <legend className="block font-medium mb-2">
                                                {t(filterLabels[filterType])}
                                            </legend>
                                            <div className="space-y-6 pt-6 sm:space-y-4 sm:pt-4">
                                                {options.map((option, optionIdx) => (
                                                    <div key={option.value} className="flex items-center"><input
    id={`${filterType}-${optionIdx}`}
    name={`${filterType}[]`}
    type="checkbox"
    checked={activeFilters[filterType].includes(option.value)}
    onChange={(e) => handleFilterChange(filterType, option.value, e.target.checked)}
    className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500"
/>
<label
    htmlFor={`${filterType}-${optionIdx}`}
    className="ml-3 text-sm text-gray-600"
>
    {option.label}
</label>
</div>
))}
</div>
</fieldset>
))}
</div>
</Disclosure.Panel>
</>
)}
</Disclosure>
)}

<main className="mx-auto max-w-2xl px-4 lg:max-w-7xl lg:px-8 bg-white pt-12">
    <div className={gridClassName}>
        {filteredAndSortedEstates.map((estate) => (
            <Link
                key={estate.id}
                to={`/estate/${estate.id}`}
                id={`estate-${estate.id}`}
                className={`${
                    selectedEstate?.id === estate.id
                        ? 'ring-2 ring-sky-500 rounded-lg'
                        : ''
                }`}
            >
                <SingleEstate estate={estate}/>
            </Link>
        ))}
    </div>

    {filteredAndSortedEstates.length === 0 && (
        <div className="text-center py-12">
            <p className="text-gray-500">
                {t('listings.noResults', 'No properties to show')}
            </p>
        </div>
    )}
</main>
</div>
);
}